import { useCallback, useEffect, useState } from "react";
import { BIDDING_TYPES, isBiddingWindowValid } from "../../utils";
import { MaterialType, OrderToSubmitRequest } from "../../types";
import { areServicesComplete } from "../Order/serviceDetail";

type StepGatingArgs = {
  activeStep: number;
  ordersToSubmit: OrderToSubmitRequest[];
  selectedType: any;
  selectedComplexType: any;
  generalBidData: any;
};

/**
 * Per-step Next-button gating for the launch-from-PR wizard, lifted verbatim
 * from OrderByRequest.tsx (`verifyThirdStep` + the activeStep effect). Also
 * derives `consultationViolation`, `isService` and `isServiceJoint`, which the
 * container uses to route step 2 and to relax the currency requirement for
 * services.
 */
export const useRequestStepGating = ({
  activeStep,
  ordersToSubmit,
  selectedType,
  selectedComplexType,
  generalBidData,
}: StepGatingArgs) => {
  const [disabled, setDisabled] = useState(true);

  const verifyThirdStep = useCallback(() => {
    const initialPrice = ordersToSubmit?.every(
      (el: any) => el.initialPrice > 0
    );
    // Quantity is optional for services; goods still require it.
    const quantityForGoods = ordersToSubmit
      ?.filter((el: any) => el.type !== MaterialType.SERVICES)
      ?.every((el: any) => el.quantity > 0);
    const startSupplyDate = ordersToSubmit?.every(
      (el: any) => el.startSupplyDate
    );
    const endSupplyDate = ordersToSubmit?.every((el: any) => el.endSupplyDate);
    const purpose = ordersToSubmit
      ?.filter((el: any) => el.type === MaterialType.SERVICES)
      ?.every((el: any) => el.purpose);
    const isService =
      ordersToSubmit?.filter((el: any) => el.type === MaterialType.SERVICES)
        ?.length > 0;
    const minimumDecrement = ordersToSubmit?.every(
      (el: any) => el.minimumDecrement > 0
    );
    if (selectedType?.value === BIDDING_TYPES.NORMAL) {
      if (isService) {
        purpose && quantityForGoods && startSupplyDate && endSupplyDate
          ? setDisabled(false)
          : setDisabled(true);
      } else {
        quantityForGoods && startSupplyDate && endSupplyDate
          ? setDisabled(false)
          : setDisabled(true);
      }
    }
    if (selectedType?.value === BIDDING_TYPES.INVERSE) {
      if (isService) {
        initialPrice && minimumDecrement && purpose && quantityForGoods
          ? setDisabled(false)
          : setDisabled(true);
      } else {
        quantityForGoods &&
        initialPrice &&
        minimumDecrement &&
        startSupplyDate &&
        endSupplyDate
          ? setDisabled(false)
          : setDisabled(true);
      }
    }
    if (selectedComplexType?.value === BIDDING_TYPES.JOINT) {
      if (selectedType?.value === BIDDING_TYPES.INVERSE) {
        quantityForGoods && initialPrice && minimumDecrement
          ? setDisabled(false)
          : setDisabled(true);
      } else {
        quantityForGoods ? setDisabled(false) : setDisabled(true);
      }
    }
    // SCRUM-114: on top of the rules above, every service needs its detail complete.
    if (!areServicesComplete(ordersToSubmit)) setDisabled(true);
  }, [ordersToSubmit, selectedType, selectedComplexType]);

  const consultationViolation =
    !!generalBidData.consultationEndValue &&
    !!generalBidData.startDateValue &&
    new Date(generalBidData.startDateValue) <=
      new Date(generalBidData.consultationEndValue);

  const isService =
    ordersToSubmit?.filter((el: any) => el.type === MaterialType.SERVICES)
      ?.length > 0;

  // Services launched as a joint solped still want a per-line supplier picker
  // (each line can target a different supplier), whereas goods joints keep the
  // shared `JointSuppliersCheckboxList`. Solpeds are all-services or all-goods
  // so `.every()` matches the actual data shape.
  const isServiceJoint =
    selectedComplexType?.value === BIDDING_TYPES.JOINT &&
    ordersToSubmit?.length > 0 &&
    ordersToSubmit.every((el: any) => el.type === MaterialType.SERVICES);

  useEffect(() => {
    if (activeStep === 1)
      selectedType !== null ? setDisabled(false) : setDisabled(true);
    if (activeStep === 2) {
      ordersToSubmit?.every((el: any) => el.suppliers?.length > 0)
        ? setDisabled(false)
        : setDisabled(true);
    }
    if (activeStep === 3) verifyThirdStep();
    if (activeStep === 4)
      generalBidData.paymentMethod &&
      isBiddingWindowValid(generalBidData) &&
      generalBidData.paymentTerm &&
      generalBidData.incoTerm &&
      (isService || generalBidData.currency) &&
      !consultationViolation
        ? setDisabled(false)
        : setDisabled(true);
  }, [
    ordersToSubmit,
    activeStep,
    selectedType,
    selectedComplexType,
    verifyThirdStep,
    // The whole form: the bidding window reads its days and times together.
    generalBidData,
    consultationViolation,
    isService,
  ]);

  return {
    disabled,
    setDisabled,
    isService,
    isServiceJoint,
    consultationViolation,
  };
};
